<template>
  <div class="study-content-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center">
            <v-btn icon class="mr-2" @click="$router.push('/app/main')">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <h1 class="headline">Contenido de estudio</h1>
          </div>
          
          <v-card class="mt-4">
            <v-card-text v-if="isLoading">
              <div class="text-center my-8">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  :size="70"
                  :width="7"
                ></v-progress-circular>
                <p class="mt-4">Cargando información del documento...</p>
              </div>
            </v-card-text>
            
            <v-card-text v-else-if="error">
              <v-alert type="error" outlined>
                {{ error }}
              </v-alert>
              <div class="text-center mt-4">
                <v-btn
                  color="primary"
                  @click="fetchDocumentInfo"
                >
                  Intentar de nuevo
                </v-btn>
              </div>
            </v-card-text>
            
            <template v-else>
              <v-card-title class="d-flex align-center">
                <v-avatar color="primary" class="mr-3" size="48">
                  <v-icon dark>{{ getFileIcon(document.mime_type) }}</v-icon>
                </v-avatar>
                <div>
                  <h2 class="text-h5">{{ document.original_name }}</h2>
                  <p class="text-caption mb-0">
                    Subido {{ formatDate(document.created_at) }}
                  </p>
                </div>
              </v-card-title>
              
              <v-divider></v-divider>
              
              <v-tabs v-model="activeTab" background-color="transparent" grow ref="tabs">
                <v-tab>
                  <v-icon left>mdi-clipboard-text-multiple-outline</v-icon>
                  Resumen
                </v-tab>
                <v-tab>
                  <v-icon left>mdi-cards-outline</v-icon>
                  Tarjetas
                </v-tab>
                <v-tab>
                  <v-icon left>mdi-help-circle-outline</v-icon>
                  Cuestionario
                </v-tab>
              </v-tabs>
              
              <v-tabs-items v-model="activeTab" class="mt-2">
                <v-tab-item>
                  <v-card flat>
                    <summary-view :document-id="documentId"></summary-view>
                  </v-card>
                </v-tab-item>
                
                <v-tab-item>
                  <v-card flat>
                    <flashcards-view :document-id="documentId"></flashcards-view>
                  </v-card>
                </v-tab-item>
                
                <v-tab-item>
                  <v-card flat>
                    <quiz-view :document-id="documentId"></quiz-view>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </template>
          </v-card>
          
          <div class="text-center mt-6" v-if="!isLoading && !error">
            <v-btn
              color="primary"
              outlined
              @click="generateAllContent"
              :loading="isGenerating"
              :disabled="isGenerating"
            >
              Regenerar todo el contenido
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import SummaryView from './SummaryView.vue';
import FlashcardsView from './FlashcardsView.vue';
import QuizView from './QuizView.vue';

export default {
  name: 'StudyContentView',
  components: {
    SummaryView,
    FlashcardsView,
    QuizView
  },
  data() {
    return {
      documentId: null,
      document: {},
      isLoading: true,
      isGenerating: false,
      error: null,
      activeTab: 0
    };
  },
  created() {
    // Obtener el ID del documento de los parámetros de la ruta
    this.documentId = this.$route.params.documentId;
    
    if (!this.documentId) {
      this.error = 'No se proporcionó un ID de documento válido';
      this.isLoading = false;
      return;
    }
    
    this.fetchDocumentInfo();
  },
  methods: {
    async fetchDocumentInfo() {
      this.isLoading = true;
      this.error = null;
      
      try {
        const response = await this.axios.get(`/documents/${this.documentId}`, {
          headers: {
            Authorization: this.$root.token.value
          }
        });
        
        if (response.data.success) {
          this.document = response.data.document;
        } else {
          this.error = response.data.error || 'No se pudo cargar la información del documento';
        }
      } catch (err) {
        console.error('Error al cargar la información del documento:', err);
        this.error = 'Error al cargar la información del documento. Por favor, inténtalo de nuevo.';
      } finally {
        this.isLoading = false;
      }
    },
    
    async generateAllContent() {
      this.isGenerating = true;
      
      try {
        const response = await this.axios.post('/generate-all-content', {
          documentId: this.documentId,
          user_id: this.$root.currentUser.userId,
          contentTypes: ['flashcards', 'quiz', 'summary']
        }, {
          headers: {
            Authorization: this.$root.token.value
          }
        });
        
        if (response.data.success) {
          this.$toast.success('Se ha generado todo el contenido correctamente');
          // Refrescar las vistas
          this.$nextTick(() => {
            // Usar la función refreshQuiz del componente QuizView si está disponible
            const quizViewRef = this.$refs.tabs && this.$refs.tabs[2] && this.$refs.tabs[2].componentInstance;
            if (quizViewRef && typeof quizViewRef.fetchQuiz === 'function') {
              quizViewRef.fetchQuiz();
            }

            // También actualizar los demás componentes
            if (this.$refs.summaryView && typeof this.$refs.summaryView.fetchSummary === 'function') {
              this.$refs.summaryView.fetchSummary();
            }
            if (this.$refs.flashcardsView && typeof this.$refs.flashcardsView.fetchFlashcards === 'function') {
              this.$refs.flashcardsView.fetchFlashcards();
            }
          });
        } else {
          this.$toast.error(response.data.error || 'Hubo un error al generar el contenido');
        }
      } catch (err) {
        console.error('Error al generar todo el contenido:', err);
        this.$toast.error('Error al generar el contenido. Por favor, inténtalo de nuevo.');
      } finally {
        this.isGenerating = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
    },
    
    getFileIcon(mimeType) {
      if (!mimeType) return 'mdi-file-outline';
      
      if (mimeType.includes('pdf')) {
        return 'mdi-file-pdf-box';
      } else if (mimeType.includes('word') || mimeType.includes('doc')) {
        return 'mdi-file-word-outline';
      } else if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
        return 'mdi-file-powerpoint-outline';
      } else if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
        return 'mdi-file-excel-outline';
      } else if (mimeType.includes('text')) {
        return 'mdi-file-document-outline';
      } else if (mimeType.includes('image')) {
        return 'mdi-file-image-outline';
      }
      
      return 'mdi-file-outline';
    }
  }
};
</script>

<style scoped>
.study-content-view {
  min-height: 100vh;
  padding-bottom: 40px;
}
</style> 